import React, { useEffect, useState, Fragment, useRef } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Box from "@material-ui/core/Box";
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Fab from "@material-ui/core/Fab";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import Typography from "@material-ui/core/Typography";

import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CodeIcon from "@material-ui/icons/Code";

import history from "../../../store/history";
import { DEV_API_DOC_GET_BY_ID_URL, DEV_API_DOC_EDIT_DATA_URL, DEV_API_DOC_DETAIL_DELETE_URL } from "../../../config";
import makeApiRequest from "../../../utils/axios/new-axios";
import { showErrorAlert, showSuccessAlert } from "../../../store/actions/alerts";
import ComponentLoader from "../../../components/loader/ComponentLoader";
import KeyValueText from "../../../components/display/KeyValueText";
import { usePageTitle } from "../../../utils/pageTitle";
import NotFound from "../../NotFound";
import StatusChip from "../../../components/api-doc/StatusChip";
import { permissions, types, checkAcl } from "../../../utils/acl";
import ApiDocEditor from "../../../components/api-doc/ApiDocEditor";
import EditApiDocForm from "../../../components/api-doc/EditApiDocForm";
import AddEditApiForm from "../../../components/api-doc/AddEditApiForm";
import MethodChip from "../../../components/api-doc/MethodChip";

const useStyles = makeStyles(theme => ({
    paper: {
        width: "100%",
        overflowX: "auto",
        marginBottom: theme.spacing(2),
        padding: "15px"
    }
}));

export default function ApiDocDetails(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const pageTitle = usePageTitle("API Docs");
    const EDIT_ACCESS = checkAcl({ p: permissions.DEV_SETTINGS, t: types.VIEW_AND_EDIT });
    const DELETE_ACCESS = checkAcl({ p: permissions.DEV_SETTINGS, t: types.VIEW_EDIT_AND_DELETE });

    const apiDocId = props.match.params.apiDocId;

    const [notFound, setNotFound] = useState(false);
    const [doc, setDoc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [openCodeEditor, setOpenCodeEditor] = useState(false);
    function handleOpenCodeEditorModal() {
        setOpenCodeEditor(true);
    }
    function handleCloseCodeEditorModal() {
        setOpenCodeEditor(false);
    }

    const [openEditDocModal, setOpenEditDocModal] = useState(false);
    function handleOpenEditDocModal() {
        setOpenEditDocModal(true);
    }
    function handleCloseEditDocModal() {
        setOpenEditDocModal(false);
    }

    const [action, setAction] = useState(null);
    const addEditApiSelectedRef = useRef(null);
    const addEditApiInitialValuesRef = useRef(null);
    const [openAddEditApiModal, setOpenAddEditApiModal] = useState(false);
    function handleOpenAddEditApiModal() {
        setOpenAddEditApiModal(true);
    }
    function handleCloseAddEditApiModal() {
        setOpenAddEditApiModal(false);
    }

    useEffect(() => {
        makeApiRequest({
            url: `${DEV_API_DOC_GET_BY_ID_URL.url}/${apiDocId}`,
            method: DEV_API_DOC_GET_BY_ID_URL.method,
        }).then(response => {
            setDoc(response.data);
            setIsLoading(false);
            pageTitle(response.data.name);
        }).catch((error) => {
            setIsLoading(false);
            if (!!error.response && error.response.status === 404) {
                setNotFound(true);
            } else {
                dispatch(
                    showErrorAlert(
                        "API Doc Fetch Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );
            }
        })
        // eslint-disable-next-line
    }, []); // [] is required to make sure that it runs only once

    const editDocButtonClick = () => {
        handleOpenEditDocModal();
    }

    const editCodeButtonClick = () => {
        handleOpenCodeEditorModal();
    }

    const addApiButtonClick = () => {
        addEditApiInitialValuesRef.current = {
            name: "",
            description: "",
            version: "1.0.0",
            path: "",
            method: "",
            status: 1
        };
        addEditApiSelectedRef.current = null;
        setAction("add");
        handleOpenAddEditApiModal();
    }

    const editApiButtonClick = (item) => {
        addEditApiInitialValuesRef.current = {
            name: item.name,
            description: item.description,
            version: item.version,
            path: item.path,
            method: item.method,
            status: item.status
        }
        addEditApiSelectedRef.current = item.id;
        setAction("edit");
        handleOpenAddEditApiModal();
    }

    const deleteApiButtonClick = (item) => {
        let confirm = prompt(`Please Enter "confirm" to delete "${item.name}"`);
        if (!!confirm && confirm.trim() === "confirm") {
            makeApiRequest({
                url: `${DEV_API_DOC_DETAIL_DELETE_URL.url}/${item.id}`,
                method: DEV_API_DOC_DETAIL_DELETE_URL.method
            }).then(() => {
                dispatch(showSuccessAlert("Deleted successfully!"));
                history.push(window.location.pathname)
            }).catch((error) => {
                dispatch(
                    showErrorAlert(
                        "API Delete Error : " +
                        (!!error.response
                            ? `${error.response.status}! - ${error.response.data.errorMessage}`
                            : error.message)
                    )
                );
            })
        } else {
            dispatch(showErrorAlert("Invalid Input"));
        }
    }


    return (
        <Fragment>
            {!notFound ? (
                <Container style={{ marginTop: "20px", padding: "10px" }}>
                    <ComponentLoader isLoading={isLoading} />
                    {!isLoading && doc && (
                        <Paper className={classes.paper}>
                            <Breadcrumbs aria-label="breadcrumb">
                                <Link color="inherit" underline="none" component={RouterLink} to="/dev/api-docs">
                                    API Docs
                                </Link>
                                <Typography color="textPrimary">{doc.name}</Typography>
                            </Breadcrumbs>
                            <br />
                            <KeyValueText
                                keyName="API Spec"
                                color="textPrimary"
                                value={`${doc.specType} (${doc.specVersion})`}
                            />
                            <KeyValueText
                                keyName="Description"
                                color="textPrimary"
                                value={doc.description || " "}
                            />
                            <KeyValueText
                                keyName="Version"
                                color="textPrimary"
                                value={doc.version || " "}
                            />
                            <StatusChip status={doc.status} />
                            <KeyValueText
                                keyName="Dev Url"
                                color="textPrimary"
                                value={doc.devUrl || " "}
                            />
                            <KeyValueText
                                keyName="Live Url"
                                color="textPrimary"
                                value={doc.liveUrl || " "}
                            />
                            {EDIT_ACCESS &&
                                <Box display="flex" flexWrap="no-wrap">
                                    <IconButton
                                        className={classes.button}
                                        aria-label="edit-doc"
                                        onClick={editDocButtonClick}
                                        size="small"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        className={classes.button}
                                        aria-label="view-doc"
                                        onClick={editCodeButtonClick}
                                        size="small"
                                    >
                                        <CodeIcon />
                                    </IconButton>
                                </Box>
                            }

                            <br />

                            {EDIT_ACCESS &&
                                <Box
                                    justifyContent="space-between"
                                    display="flex"
                                    style={{ margin: "20px" }}
                                >
                                    <Typography variant="subtitle2" gutterBottom>
                                        APIs :
                                    </Typography>
                                    <Fab
                                        size="small"
                                        color="primary"
                                        aria-label="add"
                                        onClick={addApiButtonClick}
                                    >
                                        <AddIcon />
                                    </Fab>
                                </Box>
                            }

                            {doc.details && doc.details.length > 0 ? (
                                <Table className={classes.table} size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Version</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell>Path</TableCell>
                                            <TableCell>Method</TableCell>
                                            <TableCell>Status</TableCell>
                                            {EDIT_ACCESS &&
                                                <TableCell>Actions</TableCell>
                                            }
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {doc.details.map(item => {
                                            return (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <Link
                                                            color="inherit"
                                                            component={RouterLink}
                                                            to={`/dev/api-docs/detail/${item.id}`}
                                                            underline="none"
                                                        ><b>{item.name}</b></Link>
                                                    </TableCell>
                                                    <TableCell>{item.version}</TableCell>
                                                    <TableCell>{item.description}</TableCell>
                                                    <TableCell>{item.path}</TableCell>
                                                    <TableCell>
                                                        <MethodChip method={item.method} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusChip status={item.status} />
                                                    </TableCell>
                                                    {EDIT_ACCESS &&
                                                        <TableCell style={{ paddingLeft: "0px" }}>
                                                            <Box display="flex" flexWrap="no-wrap">
                                                                <IconButton
                                                                    className={classes.button}
                                                                    aria-label="edit-api"
                                                                    onClick={() =>
                                                                        editApiButtonClick(item)
                                                                    }
                                                                    size="small"
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                                {DELETE_ACCESS &&
                                                                    <IconButton
                                                                        className={classes.button}
                                                                        aria-label="delete-api"
                                                                        onClick={() =>
                                                                            deleteApiButtonClick(item)
                                                                        }
                                                                        size="small"
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>}
                                                            </Box>

                                                        </TableCell>}
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Box justifyContent="center" display="flex">
                                    No APIs !!
                                </Box>
                            )}

                        </Paper>
                    )}
                </Container>
            ) : (
                <NotFound />
            )}
            {doc && EDIT_ACCESS && (
                <Fragment>
                    <ApiDocEditor
                        openModal={openCodeEditor}
                        handleCloseModal={handleCloseCodeEditorModal}
                        jsonString={doc.data}
                        url={`${DEV_API_DOC_EDIT_DATA_URL.url}/${apiDocId}`}
                        method={DEV_API_DOC_EDIT_DATA_URL.method}
                        returnUrl={window.location.pathname}
                        specType={doc.specType}
                        doc={{
                            specType: doc.specType,
                            specVersion: doc.specVersion,
                            name: doc.name,
                            devUrl: doc.devUrl,
                            liveUrl: doc.liveUrl,
                            description: doc.description,
                            version: doc.version
                        }}
                    />
                    <EditApiDocForm
                        apiDocId={apiDocId}
                        openModal={openEditDocModal}
                        handleCloseModal={handleCloseEditDocModal}
                        initialValues={{
                            specType: doc.specType,
                            specVersion: doc.specVersion,
                            name: doc.name,
                            description: doc.description,
                            version: doc.version,
                            devUrl: doc.devUrl,
                            liveUrl: doc.liveUrl,
                            status: doc.status
                        }}
                        returnUrl={window.location.pathname}
                    />
                    <AddEditApiForm
                        apiDocId={apiDocId}
                        apiDocDetailId={addEditApiSelectedRef.current}
                        openModal={openAddEditApiModal}
                        handleCloseModal={handleCloseAddEditApiModal}
                        initialValues={addEditApiInitialValuesRef.current}
                        returnUrl={window.location.pathname}
                        action={action}
                        specType={doc.specType}
                    />

                </Fragment>
            )}
        </Fragment>
    )
}
